import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Stethoscope, Loader2, RefreshCw, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export function DiagnoseButton({ mbl, isSuperAdmin }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [revalidating, setRevalidating] = useState(false);

  if (!isSuperAdmin) return null;

  const fetchDiag = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/diagnostics/shipment/${mbl}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al diagnosticar');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async () => {
    setOpen(true);
    setData(null);
    await fetchDiag();
  };

  const handleRevalidate = async () => {
    setRevalidating(true);
    const tid = toast.loading(`Revalidando ${mbl}... puede tardar ~30s`);
    try {
      const r = await api.post(`/diagnostics/shipment/${mbl}/revalidate`);
      setData(r.data.diagnostic_after);
      toast.success(`Revalidación completa`, { id: tid });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al revalidar', { id: tid });
    } finally {
      setRevalidating(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleOpen}
        title="Diagnóstico técnico del embarque (solo super-admin)"
        data-testid="diagnose-btn"
      >
        <Stethoscope size={16} className="mr-2 text-amber-400" />
        Diagnosticar
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="diagnose-dialog">
          <DialogHeader>
            <DialogTitle>Diagnóstico — {mbl}</DialogTitle>
            <DialogDescription>
              Vista técnica de todas las fuentes (puerto, Ferrovalle, carrier), eventos del timeline
              y qué status calcula el motor de sync ahora mismo. Si ves algo raro, click en
              {' '}«Forzar revalidación» para re-scrapear todo y volver a sincronizar.
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2 mb-3">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchDiag}
              disabled={loading || revalidating}
              data-testid="diag-refresh-btn"
            >
              {loading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
              Refrescar
            </Button>
            <Button
              onClick={handleRevalidate}
              disabled={loading || revalidating}
              data-testid="diag-revalidate-btn"
            >
              {revalidating ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Stethoscope size={14} className="mr-2" />}
              Forzar revalidación
            </Button>
          </div>

          {(loading || revalidating) && !data && (
            <div className="text-center py-12 text-sm text-muted-foreground">
              <Loader2 size={32} className="mx-auto mb-3 animate-spin" />
              {revalidating ? 'Re-scrapeando carrier + puerto + Ferrovalle...' : 'Cargando diagnóstico...'}
            </div>
          )}

          {data && (
            <div className="space-y-4 text-xs">
              <section className="border border-border rounded-sm p-3">
                <div className="font-semibold text-sm mb-2">Embarque</div>
                <div className="grid grid-cols-2 gap-1">
                  <div><span className="text-muted-foreground">Status:</span> <span className="font-mono">{data.shipment_status || '—'}</span></div>
                  <div><span className="text-muted-foreground">Actualizado por:</span> {data.shipment_status_updated_by || '—'}</div>
                  <div><span className="text-muted-foreground">Arribó:</span> {data.shipment_arrived_at || '—'}</div>
                  <div><span className="text-muted-foreground">Entregado:</span> {data.shipment_delivered_at || '—'}</div>
                  <div><span className="text-muted-foreground">ETA real:</span> {data.shipment_eta_real || '—'}</div>
                </div>
              </section>

              {(data.containers || []).map((c) => (
                <section key={c.container_number} className="border border-border rounded-sm p-3 space-y-2" data-testid={`diag-container-${c.container_number}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="font-semibold text-sm font-mono">{c.container_number}</div>
                    <div className="flex gap-3 text-xs">
                      <span><span className="text-muted-foreground">Status:</span> <span className="font-mono text-emerald-300">{c.status}</span></span>
                      <span><span className="text-muted-foreground">Semáforo:</span> <span className={
                        c.semaforo_aduanal === 'verde' ? 'text-emerald-400 font-medium' :
                        c.semaforo_aduanal === 'rojo' ? 'text-rose-400 font-medium' : 'text-muted-foreground'
                      }>{c.semaforo_aduanal || '—'}</span></span>
                      <span><span className="text-muted-foreground">Vía:</span> {c.metodo_transporte_terrestre || '—'}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <div><span className="text-muted-foreground">arrived_at:</span> {c.arrived_at || '—'}</div>
                    <div><span className="text-muted-foreground">delivered_at:</span> {c.delivered_at || '—'}</div>
                    <div><span className="text-muted-foreground">pedimento:</span> {c.numero_pedimento || '—'}</div>
                    <div><span className="text-muted-foreground">ETA Pantaco:</span> {c.eta_pantaco || '—'}</div>
                    <div><span className="text-muted-foreground">status_updated_by:</span> {c.status_updated_by || '—'}</div>
                  </div>

                  {(c.port_tracking || []).length > 0 && (
                    <div className="mt-2">
                      <div className="text-muted-foreground font-medium">Port Tracking</div>
                      {c.port_tracking.map((p, i) => (
                        <div key={i} className="ml-3 mt-1 grid grid-cols-2 gap-1">
                          <div>[{p.terminal_code}] entrada: <span className="font-mono">{p.fecha_entrada || '—'}</span></div>
                          <div>salida: <span className="font-mono">{p.fecha_salida || '—'}</span></div>
                          <div>autorización: <span className="font-mono">{p.fecha_autorizacion || '—'}</span></div>
                          <div>lib_aa: {p.liberacion_aa || '—'} · lib_nav: {p.liberacion_naviera || '—'}</div>
                          <div>scraped: {p.scraped_at || '—'}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {c.ferrovalle && (
                    <div className="mt-2">
                      <div className="text-muted-foreground font-medium">Ferrovalle</div>
                      <div className="ml-3 grid grid-cols-2 gap-1">
                        <div>arribo_est: <span className="font-mono">{c.ferrovalle.arribo_tren_estimado || '—'}</span></div>
                        <div>arribo_real: <span className="font-mono">{c.ferrovalle.arribo_tren_real || '—'}</span></div>
                        <div>salida_terminal: <span className="font-mono">{c.ferrovalle.salida_terminal || '—'}</span></div>
                        <div>plataforma: {c.ferrovalle.plataforma_arribo || '—'}</div>
                      </div>
                    </div>
                  )}

                  {c.carrier_tracking && (
                    <div className="text-muted-foreground">
                      Carrier {c.carrier_tracking.carrier_code || '—'} · {c.carrier_tracking.events_count} eventos · scraped: {c.carrier_tracking.last_scrape_at || '—'}
                    </div>
                  )}

                  {c.sync_hint && (
                    <div className="bg-muted/20 px-2 py-1 rounded text-xs">
                      <div className="text-muted-foreground">sync_hint:</div>
                      <div>port_evidence: <span className="font-mono text-amber-300">{c.sync_hint.port_status_evidence || '—'}</span></div>
                      <div>carrier_evidence: <span className="font-mono">{c.sync_hint.carrier_status_evidence || '—'}</span></div>
                      <div>port_via: {c.sync_hint.port_metodo_terrestre || '—'}</div>
                    </div>
                  )}

                  {c.sync_hint?.port_status_evidence && c.sync_hint.port_status_evidence !== c.status && (
                    <div className="bg-amber-950/30 border border-amber-700/40 rounded px-2 py-1 text-amber-300 flex items-start gap-2">
                      <AlertCircle size={14} className="shrink-0 mt-0.5" />
                      <span>
                        Discrepancia: el puerto sugiere <b>{c.sync_hint.port_status_evidence}</b> pero el contenedor está en <b>{c.status}</b>. La revalidación debería resolverlo.
                      </span>
                    </div>
                  )}

                  <div className="text-muted-foreground">{c.events_total} eventos en timeline</div>
                  {(c.events || []).length > 0 && (
                    <details className="ml-3">
                      <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">Ver últimos {c.events.length} eventos</summary>
                      <div className="mt-1 space-y-0.5 font-mono">
                        {c.events.map((e, i) => (
                          <div key={i} className="text-xs">
                            <span className="text-muted-foreground">[{e.date}]</span> {e.note}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </section>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
